
import { EventCard } from '@/components/EventCard';
import { Loader2 } from 'lucide-react';
import { VenueRecommendation, DateRecommendation } from '@/components/event-card/types';
import { UserEvent } from '@/hooks/useUserEvents';
import { Event } from '@/types/event';

interface EventListProps {
  events: (Event | UserEvent)[] | undefined;
  isLoading: boolean;
  userId: string | null;
  onArchiveToggle?: (eventId: string, currentArchiveState: boolean) => void;
  emptyMessage?: string;
}

export const EventList = ({ 
  events = [], 
  isLoading, 
  userId,
  onArchiveToggle,
  emptyMessage = 'No events found'
}: EventListProps) => {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );
  }

  if (!events.length) {
    return (
      <div className="text-center py-8 text-gray-500">
        {emptyMessage}
      </div>
    );
  }

  // Group events by status, so confirmed/booked events appear at the top
  const confirmedEvents = events.filter(
    event => event.status === 'confirmed' || event.status === 'booked'
  );
  const otherEvents = events.filter(
    event => event.status !== 'confirmed' && event.status !== 'booked'
  );
  
  // Sort each group by date (most recent first)
  const sortedEvents = [...confirmedEvents, ...otherEvents];

  return (
    <div className="space-y-4">
      {sortedEvents.map((event) => {
        const venueRecommendations: VenueRecommendation[] = event.event_venue_recommendations?.map(rec => ({
          recommendation_id: rec.recommendation_id,
          is_selected: rec.is_selected,
          venue: rec.venue || { name: '', address: '' }
        })) || [];

        const dateRecommendations: DateRecommendation[] = event.event_date_recommendations?.map(rec => ({
          recommendation_id: rec.recommendation_id,
          is_selected: rec.is_selected,
          recommended_date: rec.recommended_date,
          time_of_day: rec.time_of_day
        })) || [];
        
        const recommendations = {
          venues: {
            total: venueRecommendations.length,
            selected: venueRecommendations.filter(r => r.is_selected).length
          },
          dates: {
            total: dateRecommendations.length,
            selected: dateRecommendations.filter(r => r.is_selected).length
          }
        };

        const userEvent = event as UserEvent;

        return (
          <EventCard
            key={event.event_id}
            eventId={event.event_id}
            title={event.name}
            date={event.event_date ? new Date(event.event_date) : null}
            image={event.image_url}
            status={event.status}
            archived={event.archived}
            isHost={!userEvent.isGuest}
            isGuest={userEvent.isGuest}
            rsvpStatus={userEvent.rsvpStatus}
            inviteId={userEvent.inviteId}
            onArchiveToggle={onArchiveToggle ? () => onArchiveToggle(event.event_id, event.archived) : undefined}
            locationType={event.location_type}
            flexibleLocationDescription={event.flexible_location_description}
            preferredDates={event.preferred_dates?.map(date => new Date(date))}
            preferredVenues={event.preferred_venues}
            guests={0}
            recommendations={recommendations}
            event_venue_recommendations={venueRecommendations}
            event_date_recommendations={dateRecommendations}
            description={event.description}
            event_images={event.event_images}
            event_deadline={event.event_deadline}
          />
        );
      })}
    </div>
  );
}
